import { Eye } from "lucide-react";
import type {
  BalanceResult,
  CrossCheck,
  CrossCheckMap,
  YearSummaryEmployee,
  YearSummaryMonth,
} from "../../types/payrollAdmin.js";
import {
  aggregatePeriodCrossCheck,
  displayMismatchTooltip,
  formatComparisonPair,
  formatDisplayComparisonPair,
  formatDisplayValue,
  formatDuration,
  mismatchTooltip,
  periodDisplayValue,
  type PayrollDisplayUnit,
} from "../../routes/payrollDisplay.js";
import {
  crossCheckClass,
  hasPaidLeaveEvidence,
  PeriodTotalMetric,
} from "./MetricCells.js";

export const PeriodSummaryMobileCard = ({
  row,
  unit,
  onViewDetails,
}: {
  row: YearSummaryEmployee;
  unit: PayrollDisplayUnit;
  onViewDetails: (userId: number) => void;
}) => {
  const hoursPerDay = [...row.monthlyRows]
    .reverse()
    .find(
      (candidate) => typeof candidate.hoursPerDay === "number",
    )?.hoursPerDay;
  const periodMetric = (
    checkKey: keyof CrossCheckMap,
    valueKey: keyof YearSummaryMonth,
  ) => {
    const value = (candidate: YearSummaryMonth) =>
      typeof candidate[valueKey] === "number"
        ? (candidate[valueKey] as number)
        : null;
    const check = aggregatePeriodCrossCheck(
      row.monthlyRows,
      checkKey,
      value,
      unit,
    ) as CrossCheck | undefined;
    return {
      value: formatDisplayComparisonPair(
        check,
        periodDisplayValue(row.monthlyRows, value, unit),
        unit,
      ),
      title: displayMismatchTooltip(check, unit),
    };
  };
  const metrics = [
    {
      label: "Timesheet total",
      value: formatDisplayValue(
        periodDisplayValue(row.monthlyRows, (month) => month.timesheetHours, unit),
        unit,
      ),
    },
    {
      label: "Payslip worked",
      value: formatDisplayValue(
        periodDisplayValue(row.monthlyRows, (month) => month.payslipPaidHours, unit),
        unit,
      ),
    },
    {
      label: "Contract hours",
      value: formatDisplayValue(
        periodDisplayValue(
          row.monthlyRows,
          (month) => month.payslipNominalHours,
          unit,
        ),
        unit,
      ),
    },
    {
      label: "Overtime pot",
      value: formatDisplayValue(
        unit === "days" ? row.overtimePotDays : row.overtimePotHours,
        unit,
      ),
      title: displayMismatchTooltip(
        aggregatePeriodCrossCheck(
          row.monthlyRows,
          "overtime",
          (candidate) => candidate.timesheetHours,
          unit,
        ) as CrossCheck | undefined,
        unit,
      ),
    },
    {
      label: "Vacation remaining",
      value: formatComparisonPair(
        row.remainingVacationCrossCheck ?? undefined,
        row.remainingVacationHours,
        unit,
        hoursPerDay,
      ),
      title: mismatchTooltip(
        row.remainingVacationCrossCheck ?? undefined,
        unit,
        hoursPerDay,
      ),
    },
    { label: "Vacation", ...periodMetric("vacation", "vacationHours") },
    { label: "Doctor", ...periodMetric("doctor", "doctorHours") },
    { label: "Family care", ...periodMetric("familyCare", "familyCareHours") },
    { label: "Illness", ...periodMetric("illness", "illnessHours") },
    {
      label: "Public holidays",
      ...periodMetric("publicHoliday", "publicHolidayHours"),
    },
    ...(row.monthlyRows.some(hasPaidLeaveEvidence)
      ? [{ label: "Paid leave", ...periodMetric("paidLeave", "paidLeaveHours") }]
      : []),
  ];

  return (
    <article className="border-b border-border bg-surface p-4 last:border-b-0">
      <button
        type="button"
        onClick={() => onViewDetails(row.userId)}
        aria-label={`View period details for ${row.employeeName}`}
        title={`View period details for ${row.employeeName}`}
        className="group flex min-h-11 w-full cursor-pointer items-center justify-between gap-3 rounded-lg px-2 text-left transition-colors hover:bg-elevated focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
      >
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold text-foreground group-hover:text-accent">
            {row.employeeName}
          </span>
          <span className="mt-1 block text-xs text-foreground-muted">
            Employee code {row.payrollEmployeeCode || "not set"}
          </span>
        </span>
        <Eye
          size={16}
          aria-hidden="true"
          className="shrink-0 text-foreground-subtle group-hover:text-accent"
        />
      </button>
      <dl className="mt-4 grid grid-cols-2 gap-3 border-y border-border py-3">
        {metrics.map((metric) => (
          <div key={metric.label} className="min-w-0">
            <dt className="text-xs text-foreground-muted">{metric.label}</dt>
            <dd
              title={metric.title}
              className={`${crossCheckClass(undefined)} mt-1 truncate text-sm font-semibold tabular-nums text-foreground`}
            >
              {metric.value}
            </dd>
          </div>
        ))}
      </dl>
    </article>
  );
};

export const yearDetailTotals = (rows: YearSummaryMonth[]) => {
  const completedRows = rows.filter((row) => row.hasResult);
  const sum = (selector: (row: YearSummaryMonth) => number | null) =>
    completedRows.reduce((total, row) => total + (selector(row) ?? 0), 0);

  return {
    timesheetHours: sum((row) => row.timesheetHours),
    payslipPaidHours: sum((row) => row.payslipPaidHours),
    payslipComparableHours: sum((row) => row.payslipComparableHours),
    overtimeDeltaHours: sum((row) => row.overtimeDeltaHours),
    vacationHours: sum((row) => row.vacationHours),
    vacationTakenDays: sum((row) => row.vacationTakenDays),
    remainingVacationHours:
      [...completedRows]
        .reverse()
        .find((row) => typeof row.calculatedRemainingVacationHours === "number")
        ?.calculatedRemainingVacationHours ?? null,
    doctorHours: sum((row) => row.doctorHours),
    familyCareHours: sum((row) => row.familyCareHours ?? null),
    illnessHours: sum((row) => row.illnessHours),
    publicHolidayHours: sum((row) => row.publicHolidayHours ?? null),
    paidLeaveHours: sum((row) => row.paidLeaveHours ?? null),
  };
};

export const PeriodDetailTotals = ({
  employee,
  totals,
  unit,
}: {
  employee: YearSummaryEmployee;
  totals: ReturnType<typeof yearDetailTotals>;
  unit: PayrollDisplayUnit;
}) => {
  const rows = employee.monthlyRows ?? [];
  const hoursPerDay = [...rows]
    .reverse()
    .find((row) => typeof row.hoursPerDay === "number")?.hoursPerDay;
  const valueFor =
    (valueKey: keyof YearSummaryMonth) => (row: YearSummaryMonth) =>
      typeof row[valueKey] === "number" ? (row[valueKey] as number) : null;
  const comparison = (
    checkKey: keyof CrossCheckMap,
    valueKey: keyof YearSummaryMonth,
  ) => {
    const value = valueFor(valueKey);
    const check = aggregatePeriodCrossCheck(rows, checkKey, value, unit) as
      | CrossCheck
      | undefined;
    return {
      check,
      title: displayMismatchTooltip(check, unit),
      value: formatDisplayComparisonPair(
        check,
        periodDisplayValue(rows, value, unit),
        unit,
      ),
    };
  };
  const overtime = comparison("overtime", "overtimeDeltaHours");
  const showPaidLeave = rows.some(hasPaidLeaveEvidence);
  const vacation = comparison("vacation", "vacationHours");
  const doctor = comparison("doctor", "doctorHours");
  const familyCare = comparison("familyCare", "familyCareHours");
  const illness = comparison("illness", "illnessHours");
  const publicHoliday = comparison("publicHoliday", "publicHolidayHours");
  const paidLeave = comparison("paidLeave", "paidLeaveHours");
  const remainingVacation = employee.remainingVacationCrossCheck ?? undefined;

  return (
    <section
      aria-label="Period totals"
      className="mb-4 overflow-hidden rounded-xl border border-border bg-surface"
    >
      <div className="px-4 pt-3">
        <span
          title="Period totals"
          className="block size-2 rounded-full bg-success"
          aria-hidden="true"
        />
        <span className="sr-only">Period totals</span>
      </div>
      <div className="overflow-x-auto">
        <div className={`grid min-w-[1280px] py-4 ${showPaidLeave ? "grid-cols-12" : "grid-cols-11"}`}>
          <PeriodTotalMetric
            label="Timesheet total"
            value={formatDisplayValue(
              periodDisplayValue(rows, (row) => row.timesheetHours, unit),
              unit,
            )}
          />
          <PeriodTotalMetric
            label="Payslip worked"
            value={formatDisplayValue(
              periodDisplayValue(rows, (row) => row.payslipPaidHours, unit),
              unit,
            )}
          />
          <PeriodTotalMetric
            label="Contract hours"
            value={formatDisplayValue(
              periodDisplayValue(
                rows,
                (row) => row.payslipNominalHours,
                unit,
              ),
              unit,
            )}
          />
          <PeriodTotalMetric
            label="Overtime"
            value={formatDisplayValue(
              periodDisplayValue(rows, (row) => row.overtimeDeltaHours, unit),
              unit,
            )}
            check={overtime.check}
            title={overtime.title}
          />
          <PeriodTotalMetric
            label="Vacation"
            value={vacation.value}
            check={vacation.check}
            title={vacation.title}
          />
          <PeriodTotalMetric
            label="Overtime pot"
            value={formatDisplayValue(
              unit === "days"
                ? employee.overtimePotDays
                : employee.overtimePotHours,
              unit,
            )}
            check={
              aggregatePeriodCrossCheck(
                rows,
                "overtime",
                (row) => row.timesheetHours,
                unit,
              ) as CrossCheck | undefined
            }
          />
          <PeriodTotalMetric
            label="Vacation remaining"
            value={formatComparisonPair(
              remainingVacation,
              employee.remainingVacationHours ?? totals.remainingVacationHours,
              unit,
              hoursPerDay,
            )}
            check={remainingVacation}
            title={mismatchTooltip(remainingVacation, unit, hoursPerDay)}
          />
          <PeriodTotalMetric
            label="Doctor"
            value={doctor.value}
            check={doctor.check}
            title={doctor.title}
          />
          <PeriodTotalMetric
            label="Family care"
            value={familyCare.value}
            check={familyCare.check}
            title={familyCare.title}
          />
          <PeriodTotalMetric
            label="Illness"
            value={illness.value}
            check={illness.check}
            title={illness.title}
          />
          <PeriodTotalMetric
            label="Public holidays"
            value={publicHoliday.value}
            check={publicHoliday.check}
            title={publicHoliday.title}
          />
          {showPaidLeave && (
            <PeriodTotalMetric
              label="Paid leave"
              value={paidLeave.value}
              check={paidLeave.check}
              title={paidLeave.title}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export const hasPeriodSummaryValues = (
  employee: YearSummaryEmployee,
  unit: PayrollDisplayUnit,
) => {
  const containsVisibleNumber = (value: string) =>
    [...value.matchAll(/-?\d+(?:\.\d+)?[dh]/g)].some(
      (match) => Number.parseFloat(match[0] ?? "0") !== 0,
    );
  const comparison = (
    checkKey: keyof CrossCheckMap,
    valueKey: keyof YearSummaryMonth,
  ) => {
    const value = (row: YearSummaryMonth) =>
      typeof row[valueKey] === "number" ? (row[valueKey] as number) : null;
    const check = aggregatePeriodCrossCheck(
      employee.monthlyRows,
      checkKey,
      value,
      unit,
    ) as CrossCheck | undefined;
    return formatDisplayComparisonPair(
      check,
      periodDisplayValue(employee.monthlyRows, value, unit),
      unit,
    );
  };
  const hoursPerDay = [...employee.monthlyRows]
    .reverse()
    .find((row) => typeof row.hoursPerDay === "number")?.hoursPerDay;
  const cells = [
    formatDisplayValue(
      periodDisplayValue(employee.monthlyRows, (row) => row.timesheetHours, unit),
      unit,
    ),
    formatDisplayValue(
      periodDisplayValue(employee.monthlyRows, (row) => row.payslipPaidHours, unit),
      unit,
    ),
    formatDisplayValue(
      periodDisplayValue(
        employee.monthlyRows,
        (row) => row.payslipNominalHours,
        unit,
      ),
      unit,
    ),
    formatDisplayValue(
      unit === "days" ? employee.overtimePotDays : employee.overtimePotHours,
      unit,
    ),
    comparison("doctor", "doctorHours"),
    comparison("familyCare", "familyCareHours"),
    comparison("illness", "illnessHours"),
    comparison("publicHoliday", "publicHolidayHours"),
    comparison("paidLeave", "paidLeaveHours"),
    comparison("vacation", "vacationHours"),
    formatComparisonPair(
      employee.remainingVacationCrossCheck ?? undefined,
      employee.remainingVacationHours,
      unit,
      hoursPerDay,
    ),
  ];
  return cells.some(containsVisibleNumber);
};

export const hasCalculationValues = (
  result: BalanceResult,
  unit: PayrollDisplayUnit,
) => {
  const containsVisibleNumber = (value: string) =>
    [...value.matchAll(/-?\d+(?:\.\d+)?[dh]/g)].some(
      (match) => Number.parseFloat(match[0] ?? "0") !== 0,
    );
  const payrollActivityCells = [
    formatDuration(result.overtimeDeltaHours, unit, result.hoursPerDay),
    formatComparisonPair(
      result.crossChecks?.["vacation"],
      result.vacationHours,
      unit,
      result.hoursPerDay,
    ),
    formatComparisonPair(
      result.crossChecks?.["remainingVacation"],
      result.calculatedRemainingVacationHours,
      unit,
      result.hoursPerDay,
    ),
    formatComparisonPair(
      result.crossChecks?.["doctor"],
      result.doctorHours,
      unit,
      result.hoursPerDay,
    ),
    formatComparisonPair(
      result.crossChecks?.["familyCare"],
      result.familyCareHours ?? 0,
      unit,
      result.hoursPerDay,
    ),
    formatComparisonPair(
      result.crossChecks?.["illness"],
      result.illnessHours ?? 0,
      unit,
      result.hoursPerDay,
    ),
    formatComparisonPair(
      result.crossChecks?.["publicHoliday"],
      result.publicHolidayHours ?? 0,
      unit,
      result.hoursPerDay,
    ),
    formatComparisonPair(
      result.crossChecks?.["paidLeave"],
      result.paidLeaveHours ?? 0,
      unit,
      result.hoursPerDay,
    ),
  ];

  // A timesheet total alone is not useful in the calculation view when there is
  // no matched payslip. Keep those records hidden until the payslip is matched,
  // unless there is actual payroll activity to investigate.
  if (result.payslipRecordId === null) {
    return payrollActivityCells.some(containsVisibleNumber);
  }

  const matchedPayslipCells = [
    result.timesheetVersionNumber
      ? formatDuration(result.timesheetHours, unit, result.hoursPerDay)
      : "",
    result.payslipPaidHours === null
      ? ""
      : formatDuration(result.payslipPaidHours, unit, result.hoursPerDay),
  ];
  return [...matchedPayslipCells, ...payrollActivityCells].some(
    containsVisibleNumber,
  );
};
